import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { useNavigate } from "react-router-dom";
import TEXT from "../../../../utils/globalText";
import SmallPopupWithInput from "../../CommonSmallPopupWithInput";
import { schedulesTxnReject } from "../../../../redux/features/scheduleTransaction/index.js";
import CommonToast from "../../CommonToast/index.jsx";

import {
  errorcode,
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../../utils/configurationProvider.js";
import { ReimbursementMultiTxnDeniedApproval } from "../../../../redux/features/reimbursementTransaction/index.js";
import ICONS from "../../../../assets/Icons/index.js";

const ReimbursementDeclinePopup = ({
  handleClose,
  txnId,
  allTxnData,
  updateAllTxn,
  flow = "",
}) => {
  const services = useSelector((state) => state.refreshToken.services);
  const dispatch = useDispatch();
  const Navigate = useNavigate();
  const isSubmittingRef = useRef(false);

  const [declineReason, setDeclineReason] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [inputDisabled, setInputDisabled] = useState(false);

  const handleInputChange = (event) => {
    let value = event.target.value;

    // 1. Allow only alphanumeric + space
    value = value.replace(/[^a-zA-Z0-9\s]/g, "");

    // 2. Remove leading spaces
    value = value.replace(/^\s+/, "");

    // 3. Allow only one space between words
    value = value.replace(/\s{2,}/g, " ");

    // 4. Limit to 150 characters
    value = value.slice(
      GlobalConfiguration.lengthCheckZero,
      GlobalConfiguration.inputMaxLength,
    );

    setDeclineReason(value);
  };

  const handleTxnMultiRejecting = () => {
    setTimeout(() => {
      toast.dismiss();
    }, IntervalConfiguration.intervalExtraLarge);
  };

  const finalize = () => {
    setShowLoader(false);
    setInputDisabled(false);
    setDeclineReason("");
    handleClose();
  };

  const showLoadingToast = (txt) => {
    CommonToast({
      text: txt,
      icon: "https://q2pay.in/new_assets/gifs/inputloader_white.gif",
      variant: "var(--processing)",
      buttonNeeded: false,
      btnProps: {
        enabledBgColor: "var(--primary-bg)",
        enabledTextColor: "var(--text-primary)",
        btnText: "",
        handleFunction: () => {},
        icon: "",
        btnType: "button",
        isDisabled: false,
        hoverOption: "white",
      },
    });
  };

  const handleReject = async (id, reason) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      if (
        services?.find(
          (service) => service === "AReimbursementTxn" || service === "Admin",
        ) !== undefined
      ) {
        // Token Check
        if (!sessionStorage.getItem("q2p_token")) {
          return;
        }

        setShowLoader(true);
        setInputDisabled(true);

        showLoadingToast("Rejecting Reimbursement");
        handleTxnMultiRejecting();

        const result = await dispatch(
          ReimbursementMultiTxnDeniedApproval({
            id,
            AccountIdData: reason,
            services,
            Navigate,
          }),
        );
        const payload = result?.payload;

        // wait for loader animation
        await new Promise((resolve) =>
          setTimeout(resolve, IntervalConfiguration.intervalExtraLarge),
        );

        if (payload?.status === errorcode.errorFailed) {
          CommonToast({
            text: payload?.error,
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
            btnProps: {
              enabledBgColor: "var(--primary-bg)",
              enabledTextColor: "var(--text-primary)",
              btnText: "",
              handleFunction: () => {},
              icon: "",
              btnType: "button",
              isDisabled: false,
              hoverOption: "white",
            },
          });
          return finalize();
        }

        if (
          payload?.errors?.user_unauthorized?.[
            GlobalConfiguration.lengthCheckZero
          ]
        ) {
          Navigate("/feature_not_assigned", {
            state: { unavailableService: "AReimbursementTxn, Admin" },
          });
          return finalize();
        }

        // MULTI approval flow
        if (flow === "multiApproval" && payload) {
          if (payload?.status === false) {
            Navigate(
              "/reimbursement_transaction_dashboard/multi_approval/reimbursement_multi_transaction_error",
            );
          } else {
            Navigate(
              "/reimbursement_transaction_dashboard/multi_approval/reimbursement_multi_transaction_declined",
            );
          }
          return finalize();
        }

        // SINGLE approval flow
        const updatedData = allTxnData.map((item) => {
          if (item.txn_id !== id) return item;

          const tx =
            payload?.transactions?.[GlobalConfiguration.lengthCheckZero];
          let lifecycleStatus = "error";
          let errorMessage = "";

          // lifecycle status logic
          if (payload?.status === true) {
            lifecycleStatus =
              tx?.lifecycle_status === "rejected"
                ? "rejected"
                : tx?.lifecycle_status === "pending"
                  ? "pendingOne"
                  : "error";
          }

          // error message handling
          if (
            payload?.errors?.base?.[GlobalConfiguration.lengthCheckZero]?.errors
          ) {
            const errors =
              payload.errors.base[GlobalConfiguration.lengthCheckZero].errors;
            errorMessage = errors[GlobalConfiguration.lengthCheckZero];
          } else if (
            payload?.errors?.base?.[GlobalConfiguration.lengthCheckZero]
          ) {
            errorMessage =
              payload.errors.base[GlobalConfiguration.lengthCheckZero];
          }

          return {
            ...item,
            lifecycle_status:
              item.lifecycle_status === "pending" && payload?.status === true
                ? lifecycleStatus
                : "error",
            rejected: true,
            rejection_reason: reason,
            error: payload?.status === false ? "true" : "false",
            message: payload?.status === false ? errorMessage : "",
          };
        });

        updateAllTxn(updatedData);

        // toast results
        if (payload?.status === false) {
          const err =
            payload?.errors?.base?.[GlobalConfiguration.lengthCheckZero]
              ?.errors?.[GlobalConfiguration.lengthCheckZero] ||
            payload?.errors?.base?.[GlobalConfiguration.lengthCheckZero] ||
            "Error occurred";

          CommonToast({
            text: err,
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
            btnProps: {
              enabledBgColor: "var(--primary-bg)",
              enabledTextColor: "var(--text-primary)",
              btnText: "",
              handleFunction: () => {},
              icon: "",
              btnType: "button",
              isDisabled: false,
              hoverOption: "white",
            },
          });
        } else {
          CommonToast({
            text: "Reimbursement transactions rejected successfully.",
            icon: "https://q2pay.in/new_assets/common/approval/select_all_with_white_filled_circle.svg",
            variant: "var(--success)",
            buttonNeeded: false,
            btnProps: {
              enabledBgColor: "var(--primary-bg)",
              enabledTextColor: "var(--text-primary)",
              btnText: "",
              handleFunction: () => {},
              icon: "",
              btnType: "button",
              isDisabled: false,
              hoverOption: "white",
            },
          });
        }

        finalize();
      } else {
        Navigate("/feature_not_assigned", {
          state: {
            unavailableService: "AReimbursementTxn, Admin",
          },
        });
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      const emojiRegex =
        /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;
      if (emojiRegex.test(event.key)) {
        event.preventDefault();
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();

        if (
          !inputDisabled &&
          declineReason.trim().length >=
            GlobalConfiguration.owneraccountnormalLength
        ) {
          handleReject(txnId, declineReason);
        }
      }

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [inputDisabled, declineReason, txnId]);

  return (
    <div className="fixed top-top-base left-left-base w-full-width h-full-height z-z-index-fixed-ui bg-PopUp-BackDrop bg-opacity-dark-opacity">
      <SmallPopupWithInput
        headerTitle={TEXT.REJECT_REASON_LABEL}
        headerFontWeight="semibold"
        headerFontColor="text-primary"
        textSize="font-md"
        secondHeaderTitle={TEXT.REJECTION_REASON_PROMPT_REIMBURSEMENT}
        secondHeaderFontWeight="semibold"
        secondHeaderFontColor="text-secondary"
        secondHeaderTextSize="font-xs"
        inputPlaceholder={TEXT.REASON_10_150_CHAR}
        inputVariant={
          declineReason &&
          declineReason.length > GlobalConfiguration.accountnormalLength
            ? "verified"
            : declineReason &&
                declineReason.length < GlobalConfiguration.panLength
              ? "typing"
              : "default"
        }
        inputBgColor="var(--primary-bg)"
        inputFgColor="var(--text-secondary)"
        inputOnInput={(e) => {
          if (!showLoader) {
            handleInputChange(e);
          }
        }}
        inputValue={declineReason}
        inputFontKind="small"
        inputIsFullWidth={true}
        // End of Input Field Props
        btnOneText={TEXT.CANCEL}
        btnOneHandleFunction={() => {
          if (!showLoader) {
            handleClose();
          }
        }}
        btnOneEnabledBgColor="var(--primary-bg)"
        btnOneOutLineColor="var(--text-primary)"
        btnOneEnabledTextColor="var(--text-primary)"
        btnOneHoverOption="white"
        btnOneIsDisabled={showLoader}
        btnTwoText={TEXT.REJECT}
        btnTwoHandleFunction={() => {
          if (
            !declineReason ||
            declineReason.length < GlobalConfiguration.panLength ||
            showLoader
          ) {
            return;
          }
          handleReject(txnId, declineReason);
        }}
        btnTwoEnabledBgColor="var(--negative-act)"
        btnTwoEnabledTextColor="var(--text-primary)"
        btnTwoIsDisabled={
          !declineReason || declineReason.length < GlobalConfiguration.panLength
        }
        btnTwoDisabledBgColor="var(--text-disabled)"
        btnTwoIsLoaderNeeded={showLoader ? true : false}
        btnTwoIsLoading={showLoader ? true : false}
        btnTwoHoverOption="black"
        btnTwoInActiveVariantNeeded={true}
      />
    </div>
  );
};

export default ReimbursementDeclinePopup;
